import { Component, signal, computed, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { User, UserSearchCriteria, Institution, Department } from './user.model';
import { UserService } from './user.service';
import { RoleService } from '../role/role.service';
import { Role as RoleModel } from '../role/role.model';
import { StateManagementService } from '../../shared/services/state-management.service';
import { Loader } from '../../shared/components/loader/loader';
import { PageHeader } from '../../shared/components/page-header/page-header';
import { NotificationDialog } from '../../shared/components/notification-dialog/notification-dialog';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { DataTable, TableColumn } from '../../shared/components/data-table/data-table';
import { CardPanel } from '../../shared/components/card-panel/card-panel';
import { Button } from '../../shared/components/button/button';
import { InstitutionTransferList } from '../../shared/components/institution-transfer-list/institution-transfer-list';

@Component({
  selector: 'app-user-management',
  imports: [
    CommonModule,
    FormsModule,
    Loader,
    PageHeader,
    NotificationDialog,
    ConfirmDialog,
    DataTable,
    CardPanel,
    Button,
    InstitutionTransferList,
  ],
  templateUrl: './user-management.html',
})
export class UserManagement implements OnInit {
  @ViewChild(InstitutionTransferList) private transferList?: InstitutionTransferList;
  isCurrentUserSuperAdmin = signal(true);
  protected readonly Math = Math;
  notificationMessage = signal('');
  notificationType = signal<'success' | 'error'>('success');
  isNotificationVisible = signal(false);
  departments = signal<Department[]>([]);
  institutions = signal<Institution[]>([]);
  roles = signal<RoleModel[]>([]);
  users = signal<User[]>([]);
  adminGuardUsers = signal<User[]>([]);
  filteredUsers = signal<User[]>(this.users());
  currentPage = signal(1);
  pageSize = 10;
  selectedUser = signal<User | null>(null);
  isEditUser = signal(false);
  isAddUser = signal(false);
  firstName = signal('');
  lastName = signal('');
  emailId = signal('');
  loginSsoId = signal('');
  userType = signal<'Internal' | 'External'>('External');
  department = signal<{ id: string; name: string } | null>(null);
  isActive = signal(true);
  isSuperAdmin = signal(false);
  selectedRole = signal<{ id: string; name: string } | null>(null);
  selectedAvailable = signal<string[]>([]);
  selectedAssigned = signal<string[]>([]);
  assignedInstitutions = signal<Institution[]>([]);
  availableInstitutionSearch = signal('');
  showDeleteModal = signal(false);
  userToDelete = signal<User | null>(null);
  originalAssignedInstitutions: Institution[] = [];
  allowAllAccess = signal(false);
  canAllowAllAccess = computed(() => this.userType() === 'Internal' && !this.isSuperAdmin());
  searchText = '';
  searchTimeout: any;

  isLoading = signal(false);
  private loadingCount = 0;

  private startLoading(): void { this.loadingCount++; this.isLoading.set(true); }
  private stopLoading(): void { this.loadingCount = Math.max(0, this.loadingCount - 1); if (this.loadingCount === 0) this.isLoading.set(false); }
  tableColumns: TableColumn[] = [
    { key: 'name', label: 'Full Name', sortable: true, width: '22%' },
    { key: 'emailId', label: 'Email ID', align: 'center', sortable: true, width: '26%' },
    { key: 'department.name', label: 'Department', align: 'center', sortable: true, width: '20%' },
    { key: 'active', label: 'Active', align: 'center', sortable: true, width: '12%' },
    { key: 'superAdmin', label: 'Super Admin', align: 'center', sortable: true, width: '13%' },
  ];

  showNotification(message: string, type: 'success' | 'error' = 'success'): void {
    this.notificationMessage.set(message);
    this.notificationType.set(type);
    this.isNotificationVisible.set(true);
  }

  private userService = inject(UserService);
  private roleService = inject(RoleService);
  private stateManagementService = inject(StateManagementService);

  private getCurrentUserEmail(): string {
    const userValue = this.stateManagementService.getLocalStorage('userValue');
    if (!userValue) return '';
    try {
      const user = JSON.parse(userValue) as { email?: string };
      return user.email?.toLowerCase() ?? '';
    } catch {
      return '';
    }
  }

  /** Returns true when there are no other active Administrators besides the given email. */
  private isLastActiveAdmin(excludeEmail: string): boolean {
    const usersForGuard = this.adminGuardUsers().length > 0 ? this.adminGuardUsers() : this.users();

    return usersForGuard.filter(
      u => u.superAdmin === true && u.active === 'Yes' && u.emailId.toLowerCase() !== excludeEmail.toLowerCase()
    ).length === 0;
  }

  private refreshAdminGuardUsers(): void {
    this.userService.getUsers().subscribe({
      next: (users) => {
        const sorted = [...users].sort((a, b) =>
          String(a?.name ?? '').localeCompare(String(b?.name ?? '')),
        );
        this.adminGuardUsers.set(sorted);
      },
      error: (err) => {
        console.error('Error loading full user list for admin guard:', err);
      },
    });
  }

  ngOnInit(): void {
    this.fetchDepartments();
    this.fetchRoles();
    this.fetchUsers();
    this.refreshAdminGuardUsers();
  }

  fetchRoles(): void {
    this.startLoading();
    this.roleService.getRoles().subscribe({
      next: (roles) => {
        console.log('Roles loaded:', roles);
        this.roles.set(roles || []);
        this.stopLoading();
      },
      error: (err) => {
        console.error('Error fetching roles:', err);
        this.showNotification('Failed to load roles. Please refresh.', 'error');
        this.stopLoading();
      }
    });
  }

  activeRoles = computed(() => {
    return this.roles().filter(role => role.active === 'Yes');
  });

  fetchDepartments(): void {
    this.startLoading();
    this.userService.getDepartments().subscribe({
      next: (departments) => {
        this.departments.set(departments || []);
        const enrichedUsers = this.users().map(user => this.enrichUserNames(user));
        if (enrichedUsers.length > 0) {
          this.users.set(enrichedUsers);
          this.filteredUsers.set(this.filteredUsers().map(user => this.enrichUserNames(user)));
        }
        const selected = this.selectedUser();
        if (selected) {
          const enrichedSelected = this.enrichUserNames(selected);
          this.selectedUser.set(enrichedSelected);
          this.loadUserDetails(enrichedSelected);
        }
        this.stopLoading();
      },
      error: (err) => {
        console.error('Error fetching departments:', err);
        this.showNotification('Failed to load departments. Please refresh.', 'error');
        this.stopLoading();
      }
    });
  }

  onDepartmentChange(departmentId: string): void {
    if (departmentId) {
      const dept = this.departments().find(d => d.id === departmentId);
      if (dept) {
        this.department.set({ id: dept.id, name: dept.name });
        this.institutions.set(dept.institutions);
      } else {
        this.department.set(null);
        this.institutions.set([]);
      }
    } else {
      this.department.set(null);
      this.institutions.set([]);
    }
    this.assignedInstitutions.set([]);
    this.selectedAvailable.set([]);
    this.selectedAssigned.set([]);
    this.availableInstitutionSearch.set('');
  }

  fetchUsers(): void {
    this.onSearch();
  }

  allAvailableInstitutions = computed(() => this.institutions().map(i => i.name));

  paginatedUsers = computed(() => {
    const startIndex = (this.currentPage() - 1) * this.pageSize;
    return this.filteredUsers().slice(startIndex, startIndex + this.pageSize);
  });

  totalPages = computed(() => {
    return Math.ceil(this.filteredUsers().length / this.pageSize) || 1;
  });

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update(p => p + 1);
      this.selectFirstUserOnCurrentPage();
    }
  }

  prevPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update(p => p - 1);
      this.selectFirstUserOnCurrentPage();
    }
  }

  firstPage(): void {
    this.currentPage.set(1);
    this.selectFirstUserOnCurrentPage();
  }

  lastPage(): void {
    this.currentPage.set(this.totalPages());
    this.selectFirstUserOnCurrentPage();
  }

  private selectFirstUserOnCurrentPage(): void {
    const users = this.paginatedUsers();
    if (users.length > 0) {
      this.onSelectUser(users[0]);
    }
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
    this.selectFirstUserOnCurrentPage();
  }

  searchCriteria: UserSearchCriteria = {
    firstName: '',
    lastName: '',
    emailId: '',
    department: '',
    institution: '',
    active: '',
    superAdmin: '',
  };

  resetSearch(): void {
    this.searchCriteria = {
      firstName: '',
      lastName: '',
      emailId: '',
      department: '',
      institution: '',
      active: '',
      superAdmin: '',
    };
    this.searchInstitutions.set([]);
    this.fetchUsers();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSearch();
    }
  }

  searchInstitutions = signal<Institution[]>([]);

  onSearchDepartmentChange(): void {
    this.searchCriteria.institution = '';
    const deptName = this.searchCriteria.department;
    if (deptName) {
      const dept = this.departments().find(d => d.name === deptName);
      this.searchInstitutions.set(dept ? dept.institutions : []);
    } else {
      this.searchInstitutions.set([]);
    }
  }

  availableInstitutions = computed(() => {
    const all = this.allAvailableInstitutions();
    const assigned = this.assignedInstitutions().map(i => i.name);
    const available = all
      .filter((inst) => !assigned.includes(inst))
      .sort((a, b) => a.localeCompare(b));
    const search = this.availableInstitutionSearch().toLowerCase();
    if (!search) {
      return available;
    }
    return [...available].sort((a, b) => {
      const aMatch = a.toLowerCase().includes(search);
      const bMatch = b.toLowerCase().includes(search);
      if (aMatch && !bMatch) return -1;
      if (!aMatch && bMatch) return 1;
      return a.localeCompare(b);
    });
  });

  isEmailValid = computed(() => {
    const email = this.emailId().trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (this.userType() === 'Internal') {
      return email.endsWith('@encoura.org');
    }
    return emailRegex.test(email);
  });

  isFormValid = computed(() => {
    const firstName = this.firstName().trim();
    const lastName = this.lastName().trim();
    const email = this.emailId().trim();
    const emailValid = this.isEmailValid();
    const departmentValid = this.isSuperAdmin() || this.allowAllAccess() || !!this.department();
    const roleValid = this.isSuperAdmin() || !!this.selectedRole();
    const institutionsValid = this.isSuperAdmin() || this.allowAllAccess() || this.assignedInstitutions().length > 0;

    return (
      firstName.length > 0 &&
      lastName.length > 0 &&
      email.length > 0 &&
      emailValid &&
      departmentValid &&
      roleValid &&
      institutionsValid
    );
  });

  onSearch(onComplete?: () => void, preserveSelectedEmail?: string): void {
    this.startLoading();
    const searchCriteria = this.toIdSearchCriteria(this.searchCriteria);
    this.userService.getUsers(searchCriteria).subscribe({
      next: (users) => {
        const usersWithNames = users.map(user => this.enrichUserNames(user));

        const sorted = [...usersWithNames].sort((a, b) =>
          String(a?.name ?? '').localeCompare(String(b?.name ?? ''))
        );
        this.filteredUsers.set(sorted);
        this.users.set(sorted);
        this.refreshAdminGuardUsers();
        if (sorted.length === 0) {
          this.currentPage.set(1);
          this.selectedUser.set(null);
          this.stopLoading();
          onComplete?.();
          return;
        }

        const userToSelect = preserveSelectedEmail
          ? sorted.find((u) => u.emailId.toLowerCase() === preserveSelectedEmail.toLowerCase()) ?? sorted[0]
          : sorted[0];

        const selectedIndex = Math.max(0, sorted.findIndex((u) => u.emailId === userToSelect.emailId));
        this.currentPage.set(Math.floor(selectedIndex / this.pageSize) + 1);
        this.onSelectUser(userToSelect);
        this.stopLoading();
        onComplete?.();
      },
      error: (err) => {
        console.error('Error searching users:', err);
        this.showNotification('Search failed. Please try again.', 'error');
        this.stopLoading();
        onComplete?.();
      }
    });
  }

  private enrichUserNames(user: User): User {
    const departmentId = user.department?.id;
    const department = departmentId ? this.departments().find(d => d.id === departmentId) : undefined;
    const institutionById = new Map((department?.institutions ?? []).map(institution => [institution.id, institution]));

    return {
      ...user,
      department: departmentId
        ? { id: departmentId, name: department?.name ?? user.department?.name ?? '' }
        : null,
      assignedInstitutions: user.assignedInstitutions.map(institution =>
        institutionById.get(institution.id) ?? institution,
      ),
    };
  }

  private toIdSearchCriteria(criteria: UserSearchCriteria): UserSearchCriteria {
    const department = this.departments().find(item => item.name === criteria.department);
    const institution = department?.institutions.find(item => item.name === criteria.institution);

    return {
      ...criteria,
      department: department?.id ?? criteria.department,
      institution: institution?.id ?? criteria.institution,
    };
  }

  onSelectUser(user: User): void {
    if (user.emailId) {
      this.startLoading();
      this.userService.getUserByEmail(user.emailId).subscribe({
        next: (userDetails) => {
          const enrichedUser = this.enrichUserNames(userDetails);
          this.selectedUser.set(enrichedUser);
          this.isEditUser.set(false);
          this.isAddUser.set(false);
          this.loadUserDetails(enrichedUser);
          this.stopLoading();
        },
        error: (err) => {
          console.error('Error fetching user details:', err);
          this.loadUserDetails(user);
          this.stopLoading();
        }
      });
    }
  }

  private loadUserDetails(user: User): void {
    console.log('Loading user details:', user);
    this.firstName.set(user.firstName);
    this.lastName.set(user.lastName);
    this.emailId.set(user.emailId);
    this.loginSsoId.set(user.loginSsoId);
    this.userType.set(user.userType);
    this.department.set(user.department || null);

    if (user.role) {
      console.log('User role:', user.role);
      console.log('Available roles:', this.roles());

      const matchingRole = this.roles().find(r => r.id === user.role?.id);

      if (matchingRole) {
        console.log('Found matching role:', matchingRole);
        this.selectedRole.set({
          id: String(matchingRole.id),
          name: matchingRole.roleName
        });
      } else {
        console.warn('No matching role found for user role:', user.role);
        this.selectedRole.set(user.role);
      }
    } else {
      console.log('User has no role');
      this.selectedRole.set(null);
    }

    if (user.department) {
      const dept = this.departments().find(d => d.id === user.department!.id);
      if (dept) {
        this.institutions.set(dept.institutions);
      }
    }
    this.isActive.set(user.active === 'Yes');
    this.isSuperAdmin.set(user.superAdmin);
    this.allowAllAccess.set(user.allowAllAccess);
    this.assignedInstitutions.set([...user.assignedInstitutions]);
    this.selectedAvailable.set([]);
    this.selectedAssigned.set([]);
    this.availableInstitutionSearch.set('');
  }

  onCreateUser(): void {
    this.isAddUser.set(true);
    this.isEditUser.set(true);
    this.selectedUser.set(null);
    this.firstName.set('');
    this.lastName.set('');
    this.emailId.set('');
    this.loginSsoId.set('');
    this.userType.set('External');
    this.department.set(null);
    this.selectedRole.set(null);
    this.institutions.set([]);
    this.isActive.set(true);
    this.isSuperAdmin.set(false);
    this.allowAllAccess.set(false);
    this.assignedInstitutions.set([]);
    this.selectedAvailable.set([]);
    this.selectedAssigned.set([]);
    this.availableInstitutionSearch.set('');
  }

  onEditUser(): void {
    this.isEditUser.set(true);
    this.originalAssignedInstitutions = [
      ...this.selectedUser()?.assignedInstitutions.map(i => ({ id: i.id, name: i.name })) || []
    ];

    this.assignedInstitutions.set([
      ...this.originalAssignedInstitutions
    ]);
  }

  onSuperAdminChange(isSuper: boolean): void {
    this.isSuperAdmin.set(isSuper);
    if (isSuper) {
      this.selectedRole.set(null);
      this.allowAllAccess.set(false);
      this.assignedInstitutions.set(
        this.institutions().map(inst => ({ id: inst.id, name: inst.name }))
      );
    } else {
      this.assignedInstitutions.set([]);
    }
    this.selectedAvailable.set([]);
    this.selectedAssigned.set([]);
    this.availableInstitutionSearch.set('');
  }
  onAllowAllAccessChange(enabled: boolean): void {
    if (!this.canAllowAllAccess()) {
      this.allowAllAccess.set(false);
      return;
    }

    this.allowAllAccess.set(enabled);
    if (enabled) {
      this.department.set(null);
      this.institutions.set([]);
      this.assignedInstitutions.set([]);
      this.selectedAvailable.set([]);
      this.selectedAssigned.set([]);
    }
  }
  onUserTypeChange(type: 'Internal' | 'External'): void {
    this.userType.set(type);
    if (type === 'Internal') {
      this.onSuperAdminChange(true);
    } else {
      this.isSuperAdmin.set(false);
      this.allowAllAccess.set(false);

      if (this.isEditUser()) {
        this.assignedInstitutions.set([
          ...this.originalAssignedInstitutions
        ]);
      } else {
        this.assignedInstitutions.set([]);
      }

      this.selectedAvailable.set([]);
      this.selectedAssigned.set([]);
      this.availableInstitutionSearch.set('');
    }
  }

  onDeleteUser(user: User): void {
    // Self-delete prevention
    const currentUserEmail = this.getCurrentUserEmail();
    if (currentUserEmail && user.emailId.toLowerCase() === currentUserEmail) {
      this.showNotification('You cannot delete your own account. Please ask another Administrator to perform this action.', 'error');
      return;
    }
    // Last active administrator guard
    if (user.superAdmin === true && user.active === 'Yes' && this.isLastActiveAdmin(user.emailId)) {
      this.showNotification('Cannot delete the last active Administrator account. At least one active Administrator must remain.', 'error');
      return;
    }
    this.userToDelete.set(user);
    this.showDeleteModal.set(true);
  }

  cancelDelete(): void {
    this.showDeleteModal.set(false);
    this.userToDelete.set(null);
  }

  moveToAssigned(): void {
    const toMove = this.selectedAvailable();
    if (toMove.length === 0) {
      return;
    }
    const institutionsToAdd = this.institutions().filter(inst => toMove.includes(inst.name));
    this.assignedInstitutions.update(assigned => [...assigned, ...institutionsToAdd]);
    this.selectedAvailable.set([]);
  }

  moveToAvailable(): void {
    const toMove = this.selectedAssigned();
    if (toMove.length === 0) {
      return;
    }
    this.assignedInstitutions.update(assigned => assigned.filter(i => !toMove.includes(i.name)));
    this.selectedAssigned.set([]);
  }

  moveAllToAssigned(): void {
    const available = this.availableInstitutions();

    if (available.length === 0) return;

    const institutionsToAdd = this.institutions().filter((inst) => available.includes(inst.name));

    this.assignedInstitutions.update((assigned) => [
      ...assigned,
      ...institutionsToAdd.filter((inst) => !assigned.some((a) => a.id === inst.id)),
    ]);

    this.selectedAvailable.set([]);
  }

  moveAllToAvailable(): void {
    this.assignedInstitutions.set([]);
    this.selectedAssigned.set([]);
  }

  onAvailableInstitutionsKeydown(event: KeyboardEvent): void {
    // Ctrl + A
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
      event.preventDefault();
      this.selectedAvailable.set([...this.availableInstitutions()]);
      return;
    }
    // Ignore non-character keys
    if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
      return;
    }
    this.searchText += event.key.toLowerCase();
    clearTimeout(this.searchTimeout);
    this.availableInstitutionSearch.set(this.searchText);
    this.searchTimeout = setTimeout(() => {
      this.searchText = '';
    }, 1500);
    const match = this.availableInstitutions().find((inst) =>
      inst.toLowerCase().includes(this.searchText),
    );
    if (match) {
      this.selectedAvailable.set([match]);
    }
  }

  onAssignedInstitutionsKeydown(event: KeyboardEvent): void {
    // Ctrl + A
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
      event.preventDefault();

      if (this.isEditUser()) {
        this.selectedAssigned.set(
          this.assignedInstitutions().map(inst => inst.name)
        );
      }

      return;
    }

    // Ignore non-character keys
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.key.length !== 1
    ) {
      return;
    }

    this.searchText += event.key.toLowerCase();

    clearTimeout(this.searchTimeout);

    this.searchTimeout = setTimeout(() => {
      this.searchText = '';
    }, 1500);

    const match = this.assignedInstitutions().find(inst =>
      inst.name.toLowerCase().includes(this.searchText)
    );

    if (match) {
      this.selectedAssigned.set([match.name]);
    }
  }

  toggleAvailableSelection(inst: string): void {
    const current = this.selectedAvailable();
    if (current.includes(inst)) {
      this.selectedAvailable.set(current.filter(i => i !== inst));
    } else {
      this.selectedAvailable.set([...current, inst]);
    }
  }

  toggleAssignedSelection(inst: string): void {
    const current = this.selectedAssigned();
    if (current.includes(inst)) {
      this.selectedAssigned.set(current.filter(i => i !== inst));
    } else {
      this.selectedAssigned.set([...current, inst]);
    }
  }

  onSave(): void {
    const firstName = this.firstName().trim();
    const lastName = this.lastName().trim();
    const email = this.emailId().trim();
    if (!firstName || !lastName || !email) {
      this.showNotification('First Name, Last Name, and Email are required.', 'error');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.showNotification('Please enter a valid email address.', 'error');
      return;
    }
    const emailExists = this.users().some(u =>
      u.emailId.toLowerCase() === email.toLowerCase() &&
      u.emailId !== this.selectedUser()?.emailId
    );
    if (emailExists) {
      this.showNotification('Email address already exists.', 'error');
      return;
    }
    if (!this.isSuperAdmin() && !this.allowAllAccess() && this.assignedInstitutions().length === 0) {
      this.showNotification('At least one institution must be assigned.', 'error');
      return;
    }
    if (!this.isSuperAdmin() && !this.allowAllAccess() && !this.department()) {
      this.showNotification('Please select a Department.', 'error');
      return;
    }
    if (this.userType() === 'External' && this.isSuperAdmin()) {
      this.showNotification('External users cannot be marked as Super Admin.', 'error');
      return;
    }

    if (!this.isSuperAdmin() && !this.selectedRole()) {
      this.showNotification('Role is required for non-Super Admin users.', 'error');
      return;
    }

    const newUser: User = {
      id: this.selectedUser()?.id,
      firstName: firstName,
      lastName: lastName,
      name: `${firstName} ${lastName}`.trim(),
      emailId: email,
      active: this.isActive() ? 'Yes' : 'No',
      superAdmin: this.isSuperAdmin(),
      allowAllAccess: this.userType() === 'Internal' && !this.isSuperAdmin() && this.allowAllAccess(),
      loginSsoId: this.loginSsoId(),
      userType: this.userType(),
      department: this.department(),
      role: this.selectedRole(),
      assignedInstitutions: this.assignedInstitutions(),
    };
    if (this.isAddUser()) {
      this.startLoading();
      this.userService.createUser(newUser).subscribe({
        next: (response) => {
          const userWithId = { ...newUser, id: response.id };
          this.users.set([...this.users(), userWithId]);
          this.onSearch(() => {
            this.showNotification('User created successfully.', 'success');
          }, userWithId.emailId);
          this.isAddUser.set(false);
          this.isEditUser.set(false);
          this.availableInstitutionSearch.set('');
          this.assignedInstitutions.set([...(userWithId.assignedInstitutions ?? [])]);
          this.transferList?.scrollToTop();

          this.stopLoading();
        },
        error: (err) => {
          console.error('Error creating user:', err);
          this.showNotification('Failed to create user. Please try again.', 'error');
          this.stopLoading();
        }
      });
    } else {
      const selected = this.selectedUser();
      if (selected) {
        if (selected.emailId.toLowerCase() !== email.toLowerCase()) {
          this.showNotification('Email cannot be changed for an existing user.', 'error');
          return;
        }
        // Last active administrator guard on update
        const isCurrentlyActiveAdmin = selected.superAdmin === true && selected.active === 'Yes';
        const isBeingDeactivated = !this.isActive();
        const isLosingAdminRole = !this.isSuperAdmin();
        if (isCurrentlyActiveAdmin && (isBeingDeactivated || isLosingAdminRole) && this.isLastActiveAdmin(selected.emailId)) {
          this.showNotification('Cannot modify the last active Administrator account. At least one active Administrator must remain.', 'error');
          return;
        }
        this.startLoading();
        this.userService.updateUser(selected.emailId, newUser).subscribe({
          next: () => {
            const updated = this.users().map(u => u.emailId === selected.emailId ? newUser : u);
            this.users.set(updated);
            this.refreshAdminGuardUsers();
            this.onSearch(() => {
              this.showNotification('User updated successfully.', 'success');
            }, selected.emailId);
            this.isAddUser.set(false);
            this.isEditUser.set(false);
            this.availableInstitutionSearch.set('');
            this.assignedInstitutions.set([...(newUser.assignedInstitutions ?? [])]);
            this.transferList?.scrollToTop();
            this.stopLoading();
          },
          error: (err) => {
            console.error('Error updating user:', err);
            this.showNotification('Failed to update user. Please try again.', 'error');
            this.stopLoading();
          }
        });
      }
    }
  }

  confirmDelete(): void {
    const user = this.userToDelete();
    if (user && user.emailId) {
      this.startLoading();
      this.userService.deleteUser(user.emailId).subscribe({
        next: () => {
          this.users.set(this.users().filter(u => u.emailId !== user.emailId));
          this.refreshAdminGuardUsers();
          this.onSearch();
          if (this.selectedUser()?.emailId === user.emailId) {
            this.selectedUser.set(this.users().length > 0 ? this.users()[0] : null);
            if (this.selectedUser()) {
              this.onSelectUser(this.selectedUser()!);
            }
          }
          this.showDeleteModal.set(false);
          this.userToDelete.set(null);
          this.showNotification('User deleted successfully.', 'success');
          this.stopLoading();
        },
        error: (err) => {
          console.error('Error deleting user:', err);
          this.showNotification('Failed to delete user. Please try again.', 'error');
          this.showDeleteModal.set(false);
          this.stopLoading();
        }
      });
    } else if (user) {
      this.showNotification('Cannot delete user without email.', 'error');
      this.showDeleteModal.set(false);
    }
  }

  onCancel(): void {
    this.availableInstitutionSearch.set('');
    this.selectedAvailable.set([]);
    this.selectedAssigned.set([]);
    this.transferList?.scrollToTop();
    if (this.isAddUser()) {
      const firstUser = this.users().length > 0 ? this.users()[0] : null;
      this.isAddUser.set(false);
      this.isEditUser.set(false);
      this.selectedUser.set(firstUser);
      if (firstUser) {
        this.assignedInstitutions.set([...(firstUser.assignedInstitutions ?? [])]);
        this.onSelectUser(firstUser);
      } else {
        this.assignedInstitutions.set([]);
      }
    } else {
      const user = this.selectedUser();
      this.isEditUser.set(false);
      if (user) {
        this.assignedInstitutions.set([...(user.assignedInstitutions ?? [])]);
        this.onSelectUser(user);
      }
    }
  }

  onFormKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.isEditUser() && this.isFormValid()) {
      this.onSave();
    }
  }
}
